import { z } from 'zod';

import {
  ROUNDING_MODE,
  ROUNDING_MODE_OPTIONS,
} from '#/utils/roundingMode';

import { isValidStockReceiptHeaderCharge } from '#/modules/imsMgmt/receipts/stockReceipt/helper/formSerialize';
import { PRICE_CALC_MODE } from '#/modules/imsMgmt/receipts/stockReceipt/helper/unitPriceFromTotal';

const packageSchema = z.object({
  locationId: z.string().optional().nullable(),
  qty: z.union([z.string(), z.number()]).refine(
    (value) => value !== '' && value != null && !Number.isNaN(Number(value)) && Number(value) > 0,
    { message: '數量須大於 0' },
  ),
  remark: z.string().optional().nullable(),
});

function hasNumericValue(value) {
  return !(value === '' || value == null || Number.isNaN(Number(value)));
}

function sumPackagesQty(packages = []) {
  return packages.reduce((sum, pkg) => sum + (Number(pkg?.qty) || 0), 0);
}

const lineSchema = z.object({
  mtrId: z.string().min(1, '請選擇物料'),
  /** 顯示用；勿被 zod strip */
  mtrNo: z.string().optional().nullable(),
  mtrName: z.string().optional().nullable(),
  /** line 單位（packages 繼承）；寫入 unit_id */
  unitId: z.string().min(1, '請選擇單位'),
  /** line 單位單價（送 API） */
  unitPrice: z.union([z.string(), z.number()]).optional().nullable(),
  remark: z.string().optional().nullable(),
  unitOptions: z.array(z.any()).optional().nullable(),
  priceCalcMode: z.enum([
    PRICE_CALC_MODE.FROM_TOTAL,
    PRICE_CALC_MODE.FROM_UNIT_PRICE,
  ]).optional().nullable(),
  priceTotal: z.union([z.string(), z.number()]).optional().nullable(),
  priceInput: z.union([z.string(), z.number()]).optional().nullable(),
  packages: z.array(packageSchema).min(1, '至少一筆包裝'),
}).superRefine((line, ctx) => {
  const mode = line.priceCalcMode;
  const hasModeInput = mode === PRICE_CALC_MODE.FROM_TOTAL
    ? hasNumericValue(line.priceTotal)
    : mode === PRICE_CALC_MODE.FROM_UNIT_PRICE
      ? hasNumericValue(line.priceInput)
      : false;
  const hasPrice = hasNumericValue(line.unitPrice);

  if ((hasModeInput || hasPrice) && sumPackagesQty(line.packages) <= 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['packages'],
      message: '請填寫包裝數量合計大於 0',
    });
  }
});

/** 與 `#/utils/roundingMode` 同源。 */
export const STOCK_RECEIPT_ROUNDING_MODE_OPTIONS = ROUNDING_MODE_OPTIONS;

/** 無 refinement 的 object shape；供 `.extend()`（Zod 4 禁在 refined schema 上 overwrite）。 */
export const stockReceiptFormObjectSchema = z.object({
  receiptDate: z.string().min(1, '請填寫製造商開單日'),
  /** 建編 UI 暫隱，不驗必填 */
  expectedArrivalDate: z.string().optional().nullable(),
  warehouseId: z.string().min(1, '請選擇倉別'),
  supplierId: z.string().optional().nullable(),
  /** MANUAL create／edit 必填；PURCHASE／WO 可不帶（鎖定＋hydrate） */
  currencyId: z.string().optional().nullable(),
  /** MANUAL 必選；禁 ''→HALF_UP（見 superRefine）。PURCHASE／WO 可空或已 hydrate。 */
  roundingMode: z.string().optional().nullable(),
  remark: z.string().optional().nullable(),
  issueReason: z.string().optional().nullable(),
  customsDutyAmount: z.union([z.string(), z.number()]).optional().nullable()
    .refine(isValidStockReceiptHeaderCharge, '須為 0 以上，最多兩位小數'),
  freightAmount: z.union([z.string(), z.number()]).optional().nullable()
    .refine(isValidStockReceiptHeaderCharge, '須為 0 以上，最多兩位小數'),
  sourceType: z.enum(['MANUAL', 'PURCHASE', 'WORK_ORDER']).default('MANUAL'),
  sourceId: z.string().optional().nullable(),
  lines: z.array(lineSchema).min(1, '至少一筆明細'),
});

export const stockReceiptFormSchema = stockReceiptFormObjectSchema.superRefine((values, ctx) => {
  if (values.sourceType !== 'MANUAL') {
    return;
  }
  if (!values.supplierId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['supplierId'],
      message: '請選擇供應商',
    });
  }
  if (!values.currencyId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['currencyId'],
      message: '請選擇幣別',
    });
  }
  if (
    values.roundingMode !== ROUNDING_MODE.UP
    && values.roundingMode !== ROUNDING_MODE.DOWN
    && values.roundingMode !== ROUNDING_MODE.HALF_UP
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['roundingMode'],
      message: '請選擇進位法',
    });
  }
});

export const STOCK_RECEIPT_FIELD_LABELS = {
  receiptDate: '製造商開單日',
  expectedArrivalDate: '預計到貨日',
  warehouseId: '倉別',
  supplierId: '供應商',
  currencyId: '幣別',
  roundingMode: '進位法',
  remark: '備註',
  issueReason: '事由',
  customsDutyAmount: '關稅',
  freightAmount: '運費',
  sourceType: '來源類型',
  sourceId: '來源單號',
  lines: '入庫明細',
  'lines.mtrId': '物料',
  'lines.unitId': '單位',
  'lines.unitPrice': '單價',
  'lines.priceCalcMode': '計算方式',
  'lines.priceTotal': '總額',
  'lines.priceInput': '每單位價格',
  'lines.packages.locationId': '儲位',
  'lines.packages.qty': '數量',
};

export const STOCK_RECEIPT_LINES_TIP =
  '多選物料後確認；左側點選物料以編輯單價與包裝。須先選倉別才能選儲位。';
